import React from 'react';
import { Settings, Trash2 } from 'lucide-react';
import { FTANodeType } from '../../types/fta.types';

interface NodePropertiesPanelProps {
    selectedNode: any;
    selectedNodeId: string | null;
    updateSelectedNode: (key: string, value: any) => void;
    setNodes: any;
    setSelectedNodeId: (id: string | null) => void;
}

export const NodePropertiesPanel: React.FC<NodePropertiesPanelProps> = ({
    selectedNode,
    selectedNodeId,
    updateSelectedNode,
    setNodes,
    setSelectedNodeId
}) => {
    if (!selectedNode) return null;

    return (
        <>
            <h3 className="font-semibold text-lg text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                <Settings size={18} className="text-slate-400" />
                Node Properties
            </h3>

            <div className="space-y-4">
                <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Label</label>
                    <input
                        type="text"
                        value={selectedNode.data.label}
                        onChange={(e) => updateSelectedNode('label', e.target.value)}
                        className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 outline-none transition-colors"
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Type</label>
                    <select
                        value={selectedNode.data.type}
                        onChange={(e) => updateSelectedNode('type', e.target.value)}
                        className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 outline-none transition-colors"
                    >
                        <option value={FTANodeType.TOP_EVENT}>Top Event</option>
                        <option value={FTANodeType.INTERMEDIATE_EVENT}>Intermediate Event</option>
                        <option value={FTANodeType.BASIC_EVENT}>Basic Event</option>
                        <option value={FTANodeType.AND_GATE}>AND Gate</option>
                        <option value={FTANodeType.OR_GATE}>OR Gate</option>
                        <option value={FTANodeType.UNDEVELOPED_EVENT}>Undeveloped Event</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Icon Overlay</label>
                    <select
                        value={selectedNode.data.iconType || ''}
                        onChange={(e) => updateSelectedNode('iconType', e.target.value || undefined)}
                        className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 outline-none transition-colors"
                    >
                        <option value="">None</option>
                        <option value="car">Car / Vehicle</option>
                        <option value="engine">Engine / Mechanical</option>
                        <option value="sensor">Sensor / Signal</option>
                        <option value="cpu">ECU / Processor</option>
                        <option value="battery">Battery / Power</option>
                        <option value="warning">Warning / Alert</option>
                        <option value="plug">Plug / Connector</option>
                        <option value="network">Network / Data</option>
                        <option value="link">Link / Physical</option>
                        <option value="droplet">Droplet / Fluid</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Description</label>
                    <textarea
                        value={selectedNode.data.description || ''}
                        onChange={(e) => updateSelectedNode('description', e.target.value)}
                        rows={3}
                        className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 outline-none resize-none transition-colors"
                    />
                </div>

                {selectedNode.data.type === FTANodeType.BASIC_EVENT && (
                    <div>
                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Probability (0-1)</label>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                step="0.0001"
                                min="0"
                                max="1"
                                value={selectedNode.data.probability || 0}
                                onChange={(e) => updateSelectedNode('probability', e.target.value)}
                                className="w-full border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 outline-none font-mono transition-colors"
                            />
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Base failure probability for this event.</p>
                    </div>
                )}

                {selectedNode.data.calculatedProbability !== undefined && selectedNode.data.type !== FTANodeType.BASIC_EVENT && (
                    <div className="bg-slate-50 dark:bg-slate-700/50 p-3 rounded border border-slate-200 dark:border-slate-600">
                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Calculated Probability</label>
                        <div className="text-lg font-mono text-slate-700 dark:text-slate-200 font-semibold">
                            {selectedNode.data.calculatedProbability.toExponential(4)}
                        </div>
                    </div>
                )}

                <div className="pt-4 border-t border-slate-100 dark:border-slate-700">
                    <button
                        onClick={() => {
                            setNodes((nds: any) => nds.filter((n: any) => n.id !== selectedNodeId));
                            setSelectedNodeId(null);
                        }}
                        className="w-full flex items-center justify-center gap-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-transparent hover:border-red-200 dark:hover:border-red-800 rounded px-3 py-2 text-sm transition-colors"
                    >
                        <Trash2 size={16} />
                        Delete Node
                    </button>
                </div>
            </div>
        </>
    );
};
